import { Link, NavLink } from 'react-router';
import { LIBELLE_ETAT_PLAN, LIBELLE_STATUT_DOSSIER, LIBELLE_TYPE_OPERATION, type EtatLignePlan, type Profil, type ResumeCompte, type ResumeDossier, type ResumeOperation, type StatutDossier, type StatutOperation } from '@/api/types';
import { useSession } from '@/auth/session';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn, dateFr, montant } from '@/lib/utils';
import { Vide } from './common';

// ------------------------------------------------------------------ rôles (miroir de regles.ts côté API)
export const estResponsableTresorerie = (p: Profil[]) => p.includes('TRESORERIE') || p.includes('ADMINISTRATEUR');
export const estTresorier = (p: Profil[]) => p.includes('TRESORIER');
export const estCaissier = (p: Profil[]) => p.includes('CAISSIER');
export const estDirecteurFinancier = (p: Profil[]) => p.includes('DIRECTEUR_FINANCIER');
export const voitToutTresorerie = (p: Profil[]) => p.some((x) => x === 'TRESORERIE' || x === 'DIRECTEUR_FINANCIER' || x === 'DGA_FINANCES' || x === 'DGA_OPERATIONS' || x === 'DIRECTEUR_GENERAL' || x === 'CONTROLE_GESTION' || x === 'CHEF_COMPTABLE' || x === 'ADMINISTRATEUR');
/** Accès au module : acteurs de la trésorerie, Direction financière et Direction générale. */
export const accedeTresorerie = (p: Profil[]) => voitToutTresorerie(p) || estTresorier(p) || estCaissier(p);

// ------------------------------------------------------------------ semaines ISO
const JOUR = 86_400_000;
export function semaineIso(d: Date | string): string {
  const base = typeof d === 'string' ? new Date(`${d}T00:00:00Z`) : d;
  const date = new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth(), base.getUTCDate()));
  const jour = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - jour);
  const an = date.getUTCFullYear();
  const semaine = Math.ceil(((date.getTime() - Date.UTC(an, 0, 1)) / JOUR + 1) / 7);
  return `${an}-W${String(semaine).padStart(2, '0')}`;
}
export function lundiDe(semaine: string): string {
  const [an, w] = semaine.split('-W').map(Number) as [number, number];
  const quatreJanvier = new Date(Date.UTC(an, 0, 4));
  const jour = quatreJanvier.getUTCDay() || 7;
  return new Date(quatreJanvier.getTime() - (jour - 1) * JOUR + (w - 1) * 7 * JOUR).toISOString().slice(0, 10);
}
export const vendrediDe = (semaine: string): string => new Date(Date.parse(`${lundiDe(semaine)}T00:00:00Z`) + 4 * JOUR).toISOString().slice(0, 10);
export const decalerSemaine = (semaine: string, n: number): string => semaineIso(new Date(Date.parse(`${lundiDe(semaine)}T00:00:00Z`) + n * 7 * JOUR));
export const semaineCourante = (): string => semaineIso(new Date());
export const SEMAINE_ISO = /^\d{4}-W(0[1-9]|[1-4]\d|5[0-3])$/;
/** « S42 · 12 au 16 oct. 2026 ». */
export const libelleSemaine = (semaine: string): string => `S${semaine.slice(6)} · du ${dateFr(lundiDe(semaine))} au ${dateFr(vendrediDe(semaine))}`;
export const aujourdhui = (): string => new Date().toISOString().slice(0, 10);

/** Ajoute n jours ouvrés (lundi à vendredi) à une date AAAA-MM-JJ. */
export function plusJoursOuvres(date: string, n: number): string {
  let t = Date.parse(`${date}T00:00:00Z`);
  let restants = n;
  while (restants > 0) {
    t += JOUR;
    const j = new Date(t).getUTCDay();
    if (j >= 1 && j <= 5) restants--;
  }
  return new Date(t).toISOString().slice(0, 10);
}

// ------------------------------------------------------------------ badges
const COULEUR_DOSSIER: Record<StatutDossier, string> = {
  VALIDE: 'bg-blue-100 text-blue-800',
  CONTROLE: 'bg-violet-100 text-violet-800',
  RETOURNE: 'bg-amber-100 text-amber-900',
  TRANSMIS: 'bg-sky-100 text-sky-800',
  PREPARATION: 'bg-sky-100 text-sky-800',
  POSTE: 'bg-indigo-100 text-indigo-800',
  A_CONFIRMER: 'bg-indigo-100 text-indigo-800',
  PAYE: 'bg-emerald-100 text-emerald-800',
  RAPPROCHE: 'bg-emerald-200 text-emerald-900',
  ANNULE: 'bg-neutral-200 text-neutral-700',
  SUSPENDU: 'bg-red-100 text-red-800',
};
export const StatutDossierBadge = ({ statut, court }: { statut: StatutDossier; court?: boolean }) => (
  <Badge variant="outline" className={cn('border-transparent whitespace-nowrap', COULEUR_DOSSIER[statut])}>
    {court ? LIBELLE_STATUT_DOSSIER[statut].split(/[,(]/)[0]!.trim() : LIBELLE_STATUT_DOSSIER[statut]}
  </Badge>
);

export const DelaiBadge = ({ delai }: { delai: ResumeDossier['delai'] }) => {
  if (delai.cible === null) return null;
  return (
    <span className={cn('whitespace-nowrap text-xs', delai.niveau === 'orange' && 'font-medium text-amber-700', delai.niveau === 'rouge' && 'font-semibold text-red-700', delai.niveau === 'ok' && 'text-muted-foreground')} title={`Délai cible : ${delai.cible} j ouvré(s)`}>
      {delai.joursOuvres} j / {delai.cible} j
    </span>
  );
};

const COULEUR_PLAN: Record<EtatLignePlan, string> = { PROPOSE: 'bg-blue-100 text-blue-800', INSCRIT: 'bg-sky-100 text-sky-800', REPORTE: 'bg-amber-100 text-amber-900', PAYE: 'bg-emerald-100 text-emerald-800', SUSPENDU: 'bg-red-100 text-red-800' };
export const EtatPlanBadge = ({ etat }: { etat: EtatLignePlan }) => (
  <Badge variant="outline" className={cn('border-transparent', COULEUR_PLAN[etat])}>
    {LIBELLE_ETAT_PLAN[etat]}
  </Badge>
);

const COULEUR_OPERATION: Record<StatutOperation, string> = {
  A_VISER: 'bg-blue-100 text-blue-800',
  A_VALIDER_DF: 'bg-violet-100 text-violet-800',
  A_APPROUVER_DGA: 'bg-violet-100 text-violet-800',
  A_AUTORISER_DG: 'bg-violet-100 text-violet-800',
  VALIDEE: 'bg-sky-100 text-sky-800',
  EXECUTEE: 'bg-emerald-100 text-emerald-800',
  COMPTABILISEE: 'bg-emerald-100 text-emerald-800',
  REJETEE: 'bg-red-100 text-red-800',
};
export const StatutOperationBadge = ({ o }: { o: ResumeOperation }) => (
  <Badge variant="outline" className={cn('border-transparent', COULEUR_OPERATION[o.statut])}>
    {o.libelleStatut}
  </Badge>
);

export const ModeBadge = ({ mode }: { mode: ResumeDossier['mode'] }) => (mode ? <Badge variant="secondary">{mode === 'VIREMENT' ? 'Virement' : 'Espèces'}</Badge> : null);

export function Drapeaux({ d }: { d: ResumeDossier }) {
  const f = d.drapeaux;
  return (
    <span className="flex flex-wrap gap-1">
      {d.fed.urgence && <Badge variant="destructive">Urgent</Badge>}
      {f.horsBudget && <Badge variant="outline" className="border-red-300 text-red-700">Hors budget</Badge>}
      {f.regularisation && <Badge variant="outline" className="border-amber-300 text-amber-800">Régularisation</Badge>}
      {f.doublonPossible && !f.doublonConfirme && <Badge variant="outline" className="border-amber-300 text-amber-800">Doublon possible</Badge>}
      {f.revalideApresNote && <Badge variant="outline">Revalidé après note</Badge>}
      {d.nbReports > 0 && <Badge variant="outline">{d.nbReports} report{d.nbReports > 1 ? 's' : ''}</Badge>}
    </span>
  );
}

// ------------------------------------------------------------------ tableaux
/** Tableau des FED en trésorerie (listes, accueils, plan, caisse). */
export function TableDossiers({ dossiers, vide = 'Aucun dossier.', colonnes = ['statut', 'semaine', 'mode', 'delai'] }: { dossiers: ResumeDossier[]; vide?: string; colonnes?: Array<'statut' | 'semaine' | 'mode' | 'delai' | 'tresorier' | 'paiement'> }) {
  if (!dossiers.length) return <Vide message={vide} />;
  const a = (c: (typeof colonnes)[number]) => colonnes.includes(c);
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>FED</TableHead>
          <TableHead>Objet · bénéficiaire</TableHead>
          <TableHead className="text-right">Montant</TableHead>
          {a('statut') && <TableHead>Statut</TableHead>}
          {a('semaine') && <TableHead className="hidden md:table-cell">Semaine</TableHead>}
          {a('mode') && <TableHead className="hidden lg:table-cell">Mode · compte</TableHead>}
          {a('tresorier') && <TableHead className="hidden lg:table-cell">Chargé du paiement</TableHead>}
          {a('paiement') && <TableHead className="hidden md:table-cell">Payé le</TableHead>}
          {a('delai') && <TableHead className="hidden sm:table-cell">Délai</TableHead>}
        </TableRow>
      </TableHeader>
      <TableBody>
        {dossiers.map((d) => (
          <TableRow key={d.id}>
            <TableCell>
              <Link to={`/tresorerie/dossiers/${d.id}`} className="font-medium text-primary hover:underline">
                {d.fed.numero ?? 'FED'}
              </Link>
              <div className="text-xs text-muted-foreground">{d.fed.structureCode}</div>
            </TableCell>
            <TableCell className="max-w-[34ch]">
              <div className="truncate">{d.fed.objet}</div>
              <div className="truncate text-xs text-muted-foreground">{d.fed.beneficiaire ?? d.fed.emetteur?.nom}</div>
              <Drapeaux d={d} />
            </TableCell>
            <TableCell className="num text-right whitespace-nowrap">{montant(d.fed.montantUsd, true)}</TableCell>
            {a('statut') && (
              <TableCell>
                <StatutDossierBadge statut={d.statut} court />
              </TableCell>
            )}
            {a('semaine') && (
              <TableCell className="hidden md:table-cell whitespace-nowrap">
                {d.semaine}
                {d.semaine !== d.semaineInitiale && <div className="text-xs text-muted-foreground">initialement {d.semaineInitiale}</div>}
              </TableCell>
            )}
            {a('mode') && (
              <TableCell className="hidden lg:table-cell">
                <ModeBadge mode={d.mode} /> {d.compte && <span className="text-xs text-muted-foreground">{d.compte.code}</span>}
              </TableCell>
            )}
            {a('tresorier') && <TableCell className="hidden lg:table-cell">{d.tresorier?.nom ?? '—'}</TableCell>}
            {a('paiement') && <TableCell className="hidden md:table-cell">{dateFr(d.datePaiement)}</TableCell>}
            {a('delai') && (
              <TableCell className="hidden sm:table-cell">
                <DelaiBadge delai={d.delai} />
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export function TableOperations({ operations, vide = 'Aucune opération.' }: { operations: ResumeOperation[]; vide?: string }) {
  if (!operations.length) return <Vide message={vide} />;
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Numéro</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>Motif</TableHead>
          <TableHead className="text-right">Montant</TableHead>
          <TableHead className="hidden md:table-cell">Comptes</TableHead>
          <TableHead>Statut</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {operations.map((o) => (
          <TableRow key={o.id}>
            <TableCell>
              <Link to={`/tresorerie/operations/${o.id}`} className="font-medium text-primary hover:underline">
                {o.numero}
              </Link>
              <div className="text-xs text-muted-foreground">{dateFr(o.createdAt)}</div>
            </TableCell>
            <TableCell>{LIBELLE_TYPE_OPERATION[o.type]}</TableCell>
            <TableCell className="max-w-[32ch] truncate">
              {o.motif}
              {o.fedOrigine && <div className="text-xs text-muted-foreground">{o.fedOrigine.numero ?? 'FED sans numéro'}</div>}
            </TableCell>
            <TableCell className="num text-right whitespace-nowrap">{montant(o.montant, true)}</TableCell>
            <TableCell className="hidden md:table-cell text-xs">
              {o.compteDebite ? `${o.compteDebite.code} → ` : ''}
              {o.compteCredite?.code ?? '—'}
            </TableCell>
            <TableCell>
              <StatutOperationBadge o={o} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/** Soldes d'un compte : relevé, disponible, prévisionnel ; alerte sous le minimum. */
export function CarteCompte({ c, lien }: { c: ResumeCompte; lien?: string }) {
  const contenu = (
    <>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold">
            {c.banque} <span className="text-muted-foreground">· {c.code}</span>
          </div>
          <div className="truncate text-xs text-muted-foreground">{c.type === 'CAISSE' ? 'Caisse' : c.intitule}</div>
        </div>
        {c.sousMinimum && <Badge variant="destructive">Sous le minimum</Badge>}
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
        <dt className="text-muted-foreground">Disponible</dt>
        <dd className={cn('num text-right font-semibold', c.soldeDisponible < 0 && 'text-red-700')}>{montant(c.soldeDisponible, true)}</dd>
        <dt className="text-muted-foreground">Postés en attente</dt>
        <dd className="num text-right">{montant(c.postesEnAttente, true)}</dd>
        <dt className="text-muted-foreground">Programmés (semaine)</dt>
        <dd className="num text-right">{montant(c.programmesSemaine, true)}</dd>
        <dt className="text-muted-foreground">Prévisionnel</dt>
        <dd className={cn('num text-right font-medium', c.sousMinimum && 'text-red-700')}>{montant(c.soldePrevisionnel, true)}</dd>
      </dl>
      <div className="mt-2 text-xs text-muted-foreground">
        Relevé {c.dateReleve ? `du ${dateFr(c.dateReleve)}` : 'non saisi'} : {montant(c.soldeReleve, true)} · minimum {montant(c.soldeMinimum)}
      </div>
    </>
  );
  return lien ? (
    <Link to={lien} className="block rounded-lg border bg-card p-4 transition-colors hover:bg-accent/40">
      {contenu}
    </Link>
  ) : (
    <div className="rounded-lg border bg-card p-4">{contenu}</div>
  );
}

// ------------------------------------------------------------------ sous-navigation du module
interface Onglet {
  a: string;
  l: string;
  visible: (p: Profil[]) => boolean;
}
const ONGLETS: Onglet[] = [
  { a: '/tresorerie', l: 'Accueil', visible: accedeTresorerie },
  { a: '/tresorerie/dossiers', l: 'FED en trésorerie', visible: accedeTresorerie },
  { a: '/tresorerie/plan', l: 'Plan de décaissement', visible: accedeTresorerie },
  { a: '/tresorerie/situation', l: 'Situation du jour', visible: (p) => voitToutTresorerie(p) || estCaissier(p) },
  { a: '/tresorerie/caisse', l: 'Caisse', visible: (p) => voitToutTresorerie(p) || estCaissier(p) },
  { a: '/tresorerie/operations', l: 'Retours et nivellements', visible: accedeTresorerie },
  { a: '/tresorerie/comptes', l: 'Comptes', visible: accedeTresorerie },
  { a: '/tresorerie/rapports', l: 'Rapports', visible: voitToutTresorerie },
];

export function OngletsTresorerie() {
  const session = useSession();
  const profils = session?.utilisateur.profils ?? [];
  return (
    <nav className="mb-5 flex flex-wrap gap-1 border-b">
      {ONGLETS.filter((o) => o.visible(profils)).map((o) => (
        <NavLink key={o.a} to={o.a} end={o.a === '/tresorerie'} className={({ isActive }) => cn('-mb-px border-b-2 px-3 py-2 text-sm', isActive ? 'border-primary font-medium text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground')}>
          {o.l}
        </NavLink>
      ))}
    </nav>
  );
}
